/**
 * 온톨로지 슬라이스 자체를 Cosmograph 로 시각화하는 미니 다이어그램.
 *
 * 모델 → 노드/엣지 매핑:
 * - 각 `KindDef` → 1 노드 (id = `kind:${name}`, domain = 첫 번째 도메인? 미사용. 색은 kind.color > KIND_COLORS)
 * - 각 `ShapeDef` → 1 엣지 (source=`kind:${sourceKind}`, target=`kind:${targetKind}`, relation=shape.relation)
 * - Shape 가 없는 kind 는 외톨이 노드로 표시 (fallback UX)
 *
 * 클릭 시 onSelectKind 로 선택 정보 전달 → MetaEditPanel 에서 편집.
 */
import { useEffect, useMemo, useRef } from "react"
import type { KindDef, OntologySlice, ShapeDef } from "../../api/admin-client.ts"
import { KIND_COLORS } from "../graph/graph-colors.ts"
import { KnowledgeGraph, type KnowledgeGraphHandle } from "../graph/KnowledgeGraph.tsx"
import type { GraphEdge, GraphNode } from "../graph/types.ts"

interface OntologyDiagramProps {
	readonly slice: OntologySlice
	readonly onSelectKind?: (kind: KindDef | null) => void
	readonly onSelectShape?: (shape: ShapeDef) => void
}

function kindNodeId(name: string): string {
	return `kind:${name}`
}

/** slice 를 Cosmograph 에 넣을 GraphNode/GraphEdge 로 변환. */
function buildGraphData(slice: OntologySlice): { readonly nodes: GraphNode[]; readonly edges: GraphEdge[] } {
	const nodes: GraphNode[] = slice.kinds.map((k) => ({
		id: kindNodeId(k.name),
		name: k.displayName || k.name,
		kind: k.name,
		domain: k.category ?? "",
		content: k.description ?? ""
	}))

	const edges: GraphEdge[] = slice.shapes.map((s) => ({
		id: `${s.sourceKind}|${s.relation}|${s.targetKind}`,
		source: kindNodeId(s.sourceKind),
		target: kindNodeId(s.targetKind),
		relation: s.relation
	}))

	return { nodes, edges }
}

export function OntologyDiagram(props: OntologyDiagramProps) {
	const graphRef = useRef<KnowledgeGraphHandle>(null)
	const { nodes, edges } = useMemo(() => buildGraphData(props.slice), [props.slice])

	const kindByNodeId = useMemo(() => {
		const map = new Map<string, KindDef>()
		for (const k of props.slice.kinds) {
			map.set(kindNodeId(k.name), k)
		}
		return map
	}, [props.slice.kinds])

	// kind 별 color override — KIND_COLORS 전역 매핑에 slice 에 정의된 color 를 주입.
	// KnowledgeGraph 는 getNodeColor 에서 KIND_COLORS[node.kind] 를 참조하므로,
	// kind 이름을 key 로 override 하되, 렌더 사이클마다 baseline 을 복원해서 side-effect 를 제한한다.
	useEffect(() => {
		const snapshot = new Map<string, string | undefined>()
		for (const k of props.slice.kinds) {
			if (k.color) {
				snapshot.set(k.name, KIND_COLORS[k.name])
				KIND_COLORS[k.name] = k.color
			}
		}
		return () => {
			for (const [name, prev] of snapshot) {
				if (prev === undefined) delete KIND_COLORS[name]
				else KIND_COLORS[name] = prev
			}
		}
	}, [props.slice.kinds])

	const handleNodeSelect = (node: GraphNode | null) => {
		if (!node) {
			props.onSelectKind?.(null)
			return
		}
		const kind = kindByNodeId.get(node.id)
		if (kind) props.onSelectKind?.(kind)
	}

	if (nodes.length === 0) {
		return (
			<div className="ontology-diagram-empty">
				<div className="empty-icon" aria-hidden>
					◎
				</div>
				<h3>표시할 kind 가 없습니다</h3>
				<p>preset 을 선택하거나 하단의 "종류 추가" 로 추가하세요.</p>
			</div>
		)
	}

	// Cosmograph 2.2.1 은 edges 가 비어 있을 때 내부에서 Object.keys(null) 을 호출하며
	// "Cannot convert undefined or null to object" TypeError 를 렌더한다. Shape 가 전혀
	// 정의되지 않은 슬라이스는 간단한 kind 리스트로 fallback — Diagram 이 의미를 못 가질
	// 상태라 Cosmograph 자체를 로드하지 않는다.
	if (edges.length === 0) {
		return (
			<div className="ontology-diagram-empty">
				<div className="empty-icon" aria-hidden>
					◎
				</div>
				<h3>Shape 가 정의되지 않았습니다</h3>
				<p>
					현재 {nodes.length}개의 kind 가 고립되어 있습니다. 하단의 "관계 추가" 로 연결을 추가하면
					다이어그램이 나타납니다.
				</p>
				<ul className="ontology-diagram-isolated-list">
					{props.slice.kinds.map((k) => (
						<li key={k.name}>
							<button
								type="button"
								className="ontology-diagram-isolated-item"
								onClick={() => props.onSelectKind?.(k)}
							>
								<span
									className="ontology-diagram-isolated-swatch"
									style={{ background: k.color ?? KIND_COLORS[k.name] ?? "var(--pk-border)" }}
									aria-hidden
								/>
								<span className="ontology-diagram-isolated-name">{k.displayName || k.name}</span>
								{k.category ? <span className="ontology-diagram-isolated-cat">{k.category}</span> : null}
							</button>
						</li>
					))}
				</ul>
			</div>
		)
	}

	return (
		<div className="ontology-diagram-surface">
			<KnowledgeGraph ref={graphRef} nodes={nodes} edges={edges} onNodeSelect={handleNodeSelect} />
			<div className="ontology-diagram-legend">
				<span className="muted" style={{ fontSize: 11 }}>
					노드 = Kind · 엣지 = Shape (source-kind → target-kind · relation)
				</span>
				<button type="button" className="btn btn-ghost btn-sm" onClick={() => graphRef.current?.fitView()}>
					맞춤 보기
				</button>
			</div>
		</div>
	)
}
